import styled from "styled-components";
import colors from "../constants/colors";
import { IconButton, SxProps, Theme } from "@mui/material";

export const Container = styled("div")({
  background: `
    radial-gradient(circle at 20% 10%, rgba(116, 105, 182, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 80% 90%, rgba(173, 136, 198, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(225, 175, 209, 0.03) 0%, transparent 70%),
    ${colors.BACKGROUND}
  `,
  borderRadius: "15px",
  padding: "1rem",
  color: colors.BLACK,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.5rem",
  direction: "rtl",
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "200px",
    background: `linear-gradient(180deg, rgba(116, 105, 182, 0.03) 0%, transparent 100%)`,
    borderRadius: "15px 15px 0 0",
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },
});

export const SmallText = styled("div")({
  fontSize: "13px",
});

export const ErrorMessage = styled("div")({
  color: colors.RED,
  fontSize: "15px",
  fontWeight: 600,
});

export const TextIconContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "0.5rem",
});

export const IconContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginLeft: "0.5rem",
});

export const Title = styled("h1")({
  fontSize: "clamp(1.5rem, 4.5vw, 1.75rem)",
  fontWeight: 800,
  background: colors.GRADIENT_PURPLE,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  direction: "rtl",
  letterSpacing: "-0.01em",
  lineHeight: 1.2,
  marginBottom: "0.75rem",
  marginTop: "0.5rem",
  textAlign: "center",
});

export const Subtitle = styled("h2")<{
  $noMarginBottom?: boolean;
}>(({ $noMarginBottom }) => ({
  fontSize: "clamp(1rem, 3vw, 1.25rem)",
  fontWeight: 600,
  color: colors.GREY_7,
  maxWidth: "300px",
  textAlign: "center",
  marginBottom: $noMarginBottom ? "0" : "1rem",
  lineHeight: 1.5,
  marginTop: 0,
}));

export const InfoText = styled("p")({
  fontSize: "14px",
  fontWeight: 400,
  color: colors.GREY_6,
  marginBottom: "1.5rem",
  maxWidth: "300px",
  lineHeight: 1.6,
  marginTop: 0,
});

export const ContentWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-evenly",
  width: "100%",
});

export const Content = styled("p")({
  fontSize: "18px",
  marginBottom: "1rem",
  color: colors.GREY_6,
  maxWidth: "250px",
});

export const ButtonWrapper = styled("div")({
  display: "flex",
  justifyContent: "center",
  width: "100%",
});

export const FormWrapper = styled("form")({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
  width: "100%",
});

export const FieldWrapper = styled("div")({
  display: "flex",
  flexDirection: "column",
  textAlign: "right",
});

export const PleaseWaitContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

export const chipSx = (selected: boolean): SxProps<Theme> => ({
  backgroundColor: selected ? colors.PURPLE_LIGHT : colors.GREY_2,
  color: selected ? colors.WHITE : colors.BLACK,
  fontSize: "0.875rem", // Smaller, more professional font size
  fontWeight: 500, // Medium weight for professional look
  letterSpacing: "0.02em", // Slight letter spacing for readability
  "&:hover": {
    backgroundColor: selected ? colors.PURPLE_LIGHT : colors.GREY_2,
  },
  "&:focus-visible": {
    outline: `3px solid ${colors.PURPLE}`,
    outlineOffset: "2px",
  },
});

export const ChipWrapper = styled("div")({
  width: "auto",
});

export const ChipServiceDescription = styled("div")({
  fontSize: "16px",
  fontWeight: 700,
});

export const LinkWrapper = styled("div")({
  backgroundColor: colors.GREY_1,
  padding: "0.5rem 0.625rem",
  borderRadius: "6px",
  fontFamily: "monospace",
  wordBreak: "break-all",
  color: colors.BLACK,
  fontSize: "0.8125rem",
  flex: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export const LinkButton = styled("a")<{ $disabled?: boolean }>(
  ({ $disabled }) => ({
    fontSize: "13px",
    color: $disabled ? colors.GREY_4 : colors.PURPLE_LIGHT,
    cursor: $disabled ? "not-allowed" : "pointer",
    textDecoration: $disabled ? "none" : "underline",
    display: "inline-block",
    lineHeight: "10px",
    opacity: $disabled ? 0.6 : 1,
    pointerEvents: $disabled ? "none" : "auto",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      color: $disabled ? colors.GREY_4 : colors.PURPLE,
    },
  }),
);

export const Disclaimer = styled("div")({
  width: "100%",
  textAlign: "right",
  fontSize: "12px",
  marginTop: "2rem",
});

export const StyledLinkContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.5rem",
  flexDirection: "row",
  width: "100%",
});

export const CopyButton = styled("button")<{ $copied: boolean }>(
  ({ $copied }) => ({
    backgroundColor: $copied ? colors.PURPLE_LIGHTEST : colors.PURPLE,
    color: colors.WHITE,
    border: "none",
    borderRadius: "6px",
    padding: "0.5rem 0.75rem",
    fontSize: "0.8125rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: $copied ? "not-allowed" : "pointer",
    transition: "background-color 0.3s ease",
    flexShrink: 0,
    minWidth: "70px",
    whiteSpace: "nowrap",
    fontWeight: 600,

    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  }),
);

export const MessageText = styled("div")({
  color: colors.RED,
  fontSize: "15px",
  fontWeight: 600,
  marginTop: "8px",
});

export const ModalBox = styled("div")({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "300px",
  height: "100vh",
  overflow: "auto",
  backgroundColor: colors.WHITE,
  borderRadius: "8px",
  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
  padding: "1rem",
  outline: "none",
  direction: "rtl",
});

export const ModalHeader = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  marginBottom: "0.5rem",
});

export const CloseButton = styled(IconButton)({
  color: colors.GREY_7,
});

export const WarningIcon = styled("span")({
  display: "inline-flex",
  justifyContent: "center",
  alignItems: "center",
  marginLeft: "0.5rem",
  width: "16px",
  height: "16px",
  borderRadius: "50%",
  backgroundColor: colors.RED,
  color: colors.WHITE,
  fontSize: "12px",
  fontWeight: "bold",
});

export const SlotButtons = styled("div")({
  display: "flex",
  gap: "0.375rem",
  flexShrink: 0,
});
